export const ALLOWED_IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);

export const ALLOWED_TEXT_ATTACHMENT_MIME_TYPES = new Set([
  "application/javascript",
  "application/json",
  "application/sql",
  "application/toml",
  "application/x-httpd-php",
  "application/x-sh",
  "application/x-toml",
  "application/x-yaml",
  "application/xml",
  "application/yaml",
]);

export const ALLOWED_DOCUMENT_MIME_TYPES = new Set([
  "application/pdf",
  "text/plain",
  ...ALLOWED_TEXT_ATTACHMENT_MIME_TYPES,
]);

export const ALLOWED_MESSAGE_ATTACHMENT_MIME_TYPES = new Set([
  ...ALLOWED_IMAGE_MIME_TYPES,
  ...ALLOWED_DOCUMENT_MIME_TYPES,
]);

export const ALLOWED_AUDIO_MIME_TYPES = new Set([
  "audio/webm",
  "audio/wav",
  "audio/mp4",
  "audio/mpeg",
  "audio/mp3",
  "audio/ogg",
]);

/**
 * Draft upload bucket for surfaces that must upload an attachment before the
 * real session row exists. The one-shot composer creates the session and sends
 * its first prompt in the same API request, so attachment objects are staged
 * under this sentinel and then authorized by org + explicit draft marker.
 */
export const ONE_SHOT_DRAFT_ATTACHMENT_SESSION_ID = "oneshot";

/**
 * Draft upload bucket for dashboard-level voice dictation. Kept shared so
 * upload authorization and standalone clients do not silently drift.
 */
export const DASHBOARD_DRAFT_ATTACHMENT_SESSION_ID = "dashboard";

export const ATTACHMENT_LIMITS = {
  maxRawUploadBytes: 10 * 1024 * 1024,
  maxNormalizedImageBytes: 4 * 1024 * 1024,
  maxAudioUploadBytes: 20 * 1024 * 1024,
  maxImagesPerMessage: 5,
  maxTotalNormalizedBytesPerMessage: 18 * 1024 * 1024,
  maxReadUrlBatchKeys: 50,
  maxLongEdgePixels: 1568,
} as const;

export function normalizeAttachmentMime(mime: string | null | undefined): string {
  return (mime ?? "").split(";")[0]?.trim().toLowerCase() ?? "";
}

export function isAllowedImageMime(mime: string): boolean {
  return ALLOWED_IMAGE_MIME_TYPES.has(normalizeAttachmentMime(mime));
}

export function isAllowedTextAttachmentMime(mime: string): boolean {
  const normalized = normalizeAttachmentMime(mime);
  if (!normalized) return false;
  return normalized.startsWith("text/") || ALLOWED_TEXT_ATTACHMENT_MIME_TYPES.has(normalized);
}

export function isAllowedDocumentMime(mime: string): boolean {
  const normalized = normalizeAttachmentMime(mime);
  return normalized === "application/pdf" || isAllowedTextAttachmentMime(normalized);
}

export function isAllowedMessageAttachmentMime(mime: string): boolean {
  const normalized = normalizeAttachmentMime(mime);
  return isAllowedImageMime(normalized) || isAllowedDocumentMime(normalized);
}

export function isAllowedAudioMime(mime: string): boolean {
  return ALLOWED_AUDIO_MIME_TYPES.has(normalizeAttachmentMime(mime));
}
